<template>
  <component
    :is="fieldComponent"
    v-bind="
      mergeProps(
        {
          ...$props,
          ...$attrs,
          modelValue: internalValue,
          'onUpdate:model-value': (value: unknown) => {
            internalValue = value;
          },
          onBlur,
          variant: variant || 'outlined',
          validation: undefined,
          debounce: undefined,
          counter: undefined,
        },
        attrs,
      )
    "
    ref="el"
    v-set-test-id:[testIdSelector]="[
      componentName,
      testId || getModelExpression(),
    ]"
  >
    <template v-for="(_, name) in $slots" #[name]="slotData">
      <slot v-if="name" :name="name" v-bind="slotData || {}" />
    </template>
  </component>
</template>

<script lang="ts">
import type { BaseValidation } from '@vuelidate/core';
import { isEqual } from 'underscore';
import type { ComputedRef, PropType, Ref, WritableComputedRef } from 'vue';
import { computed, defineComponent, mergeProps, ref, watch } from 'vue';
import debounce from '@/common/debounce';
import {
  vSetTestId,
  getModelExpression,
  actionableComponentProps,
} from '@/common/components/ActionableComponent';

function useDebounce<Input = void>(
  wait: Ref<number>,
  job: (input: Input) => void,
) {
  let jobDebounced = (value: Input) => job(value);
  if (wait.value > 0) {
    jobDebounced = debounce((value: Input) => job(value), wait.value);
  }

  watch<number>(wait, (v: number) => {
    if (!v || v < 1) {
      jobDebounced = (value: Input) => job(value);
      return;
    }
    jobDebounced = debounce((value: Input) => job(value), v);
  });
  return (value: Input) => jobDebounced(value);
}

export function useValue<Type = unknown>(
  props: {
    modelValue?: Type;
    modelModifiers?: Record<string, boolean>;
    debounce?: number;
  },
  emit: (event: 'update:model-value', v: Type | undefined) => void,
  converter?: (v: unknown) => Type | undefined,
): WritableComputedRef<Type | undefined> {
  const internalValue = ref<Type | undefined>();
  const valueRef = computed<Type | undefined>({
    set: (input: Type | undefined) => {
      internalValue.value = converter ? converter(input) : input;
    },
    get: () => internalValue.value,
  });
  valueRef.value = props.modelValue;

  watch(
    () => props.modelValue,
    value => {
      if (isEqual(value, valueRef.value)) {
        return;
      }
      valueRef.value = value;
    },
  );

  const emitValue = useDebounce<Type | undefined>(
    computed(() => props.debounce || 0),
    value => {
      if (isEqual(value, props.modelValue)) {
        return;
      }
      emit('update:model-value', value);
    },
  );

  watch<Type | undefined>(internalValue, value => {
    emitValue(value === null ? undefined : value);
  });

  return valueRef;
}

export function getErrorMessage(
  validation: BaseValidation,
): string | undefined {
  if (!validation) {
    return undefined;
  }
  if (!validation.$errors[0]) {
    return undefined;
  }
  const message = validation.$errors[0].$message;
  return typeof message === 'object'
    ? message.value
    : message || 'Unknown error';
}

export function getAttributes(props: {
  label?: string;
  counter?: boolean;
  validation?: BaseValidation;
}): ComputedRef<{
  counter?: boolean;
  label?: string;
  errorMessage?: [string];
}> {
  return computed(() => {
    if (props.validation) {
      const errorMessage = getErrorMessage(props.validation);
      return {
        counter:
          props.counter !== undefined
            ? props.counter
            : !!props.validation.maxLength || !!props.validation.minLength,
        label: [
          props.label,
          props.validation.required && props.label ? '*' : '',
        ]
          .filter(Boolean)
          .join(' '),
        errorMessages:
          props.validation.$dirty && errorMessage ? [errorMessage] : undefined,
      };
    }

    return {
      counter: props.counter,
      label: props.label,
    };
  });
}

export type FormFieldProps<Value = unknown> = {
  modelValue?: Value;
  modelModifiers?: Record<string, boolean>;
  items?: unknown[];
  label?: string;
  debounce?: number;
  validation?: BaseValidation;
  counter?: boolean;
  variant?: 'underlined' | 'outlined' | 'filled' | 'solo' | 'plain';
};

export type FormFieldEmits<Value = unknown> = {
  (e: 'blur'): void;
  (e: 'update:model-value', v?: Value | undefined): void;
};

export const formFieldProps = {
  modelValue: [String, Number, Object, Array, Boolean] as PropType<unknown>,
  modelModifiers: Object as PropType<Record<string, boolean>>,
  items: Array as PropType<unknown[]>,
  label: String,
  debounce: Number,
  validation: Object as PropType<BaseValidation | undefined>,
  counter: Boolean,
  variant: String as PropType<
    'underlined' | 'outlined' | 'filled' | 'solo' | 'plain'
  >,
};

export const formFieldEmits = {
  blur: () => true,
  'update:model-value': (v?: unknown | undefined) => v,
};

export const getOnBlur =
  (props: { validation?: BaseValidation }, emit: { (event: 'blur'): void }) =>
  () => {
    props.validation?.$touch();
    emit('blur');
  };

const FormField = defineComponent({
  directives: { setTestId: vSetTestId },
  props: { ...actionableComponentProps, ...formFieldProps },
  emits: formFieldEmits,
  setup(props, { emit }) {
    const internalValue = useValue(props, emit);
    const attrs = getAttributes(props);
    const onBlur = getOnBlur(props, emit);
    const componentName = 'UnknownFormField';

    return { internalValue, attrs, onBlur, componentName, getModelExpression };
  },
  computed: {
    fieldComponent() {
      return 'input';
    },
    testIdSelector() {
      return 'input:not([tabindex="-1"])';
    },
  },
  methods: { mergeProps },
});

export default FormField;
</script>
